import React, { useRef, useState, useEffect } from 'react';
import { Play, Pause, Maximize, Volume2, VolumeX } from 'lucide-react';

/**
 * VideoPlayer Component with fullscreen support
 * - Uses native controls on mobile for better UX
 * - Custom overlay controls on desktop
 */
const VideoPlayer = ({ 
  src, 
  poster, 
  title = "Video",
  className = "",
  autoPlay = false,
  showControls = true,
  compact = false
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(autoPlay);
  const [showOverlay, setShowOverlay] = useState(!autoPlay);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check if it's a YouTube URL
  const isYouTube = src?.includes('youtube.com') || src?.includes('youtu.be');
  
  // Convert YouTube URL to embed format
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    let videoId = '';
    if (url.includes('youtube.com/watch')) {
      videoId = new URL(url).searchParams.get('v');
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    } else if (url.includes('youtube.com/embed/')) {
      return url;
    }
    return videoId ? `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1` : url;
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
      setShowOverlay(false);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    const element = containerRef.current;
    if (!element) return;

    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if (element.webkitRequestFullscreen) {
      element.webkitRequestFullscreen();
    } else if (element.msRequestFullscreen) {
      element.msRequestFullscreen();
    }
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
    setShowOverlay(true);
  };

  if (!src) {
    return null;
  }

  // Aspect ratio class based on compact mode
  const aspectClass = compact ? 'aspect-[16/10]' : 'aspect-video';

  // YouTube embed - works great on mobile with native YouTube controls
  if (isYouTube) {
    return (
      <div 
        ref={containerRef}
        className={`relative w-full ${aspectClass} rounded-lg overflow-hidden bg-black ${className}`}
      >
        <iframe
          src={getYouTubeEmbedUrl(src)}
          title={title}
          className="w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
        />
      </div>
    );
  }

  // Native HTML5 video player
  // On mobile: use native controls (better UX, no overlay issues)
  // On desktop: use custom overlay controls
  return (
    <div 
      ref={containerRef}
      className={`relative w-full ${aspectClass} rounded-lg overflow-hidden bg-black group ${className}`}
      onMouseEnter={() => !isMobile && showControls && setShowOverlay(true)}
      onMouseLeave={() => !isMobile && showControls && isPlaying && setShowOverlay(false)}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        className="w-full h-full object-cover"
        muted={isMuted}
        autoPlay={autoPlay}
        playsInline
        controls={isMobile} // Native controls on mobile
        onEnded={handleVideoEnd}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onClick={!isMobile ? togglePlay : undefined}
      />

      {/* Play/Pause Overlay - Desktop only */}
      {!isMobile && showOverlay && showControls && (
        <div 
          className="absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity cursor-pointer"
          onClick={togglePlay}
        >
          <button
            className="p-4 bg-white/90 hover:bg-white rounded-full shadow-lg transition-transform hover:scale-110"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <Pause className="h-8 w-8 text-slate-900" />
            ) : (
              <Play className="h-8 w-8 text-slate-900 ml-1" />
            )}
          </button>
        </div>
      )}

      {/* Bottom Controls - Desktop only */}
      {!isMobile && showControls && (
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={togglePlay}
                className="p-1.5 hover:bg-white/20 rounded transition-colors text-white"
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
              </button>
              <button
                onClick={toggleMute}
                className="p-1.5 hover:bg-white/20 rounded transition-colors text-white"
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
              </button>
            </div>
            <button
              onClick={toggleFullscreen}
              className="p-1.5 hover:bg-white/20 rounded transition-colors text-white"
              aria-label="Fullscreen"
            >
              <Maximize className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
